// GoogleDriveUploadModal.tsx - Updated for both video types
import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  uploadVideoToGoogleDrive,
  hasGoogleDriveScope,
  initiateGoogleDriveSignIn,
  setGoogleDriveScopeGranted,
  updateGeneratedVideoWithDriveUrl,
  updateIVAvatarVideoWithDriveUrl, // We'll create this
} from "../../client/api";
import { useNotification } from "../../context/NotificationContext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useGlobalStyles } from "../../theme/global";
import AddToDriveIcon from "@mui/icons-material/AddToDrive";
import { AnyVideo } from "../../context/types";

interface GoogleDriveUploadModalProps {
  open: boolean;
  onClose: () => void;
  video: AnyVideo | null;
  userId: string | undefined;
  onDriveUploadSuccess?: (videoId: string, driveUrl: string) => void;
  /** Override the URL that gets uploaded (e.g. Submagic-rendered video instead of the original) */
  overrideVideoUrl?: string;
}

const GoogleDriveUploadModal: React.FC<GoogleDriveUploadModalProps> = ({
  open,
  onClose,
  video,
  userId,
  onDriveUploadSuccess,
  overrideVideoUrl,
}) => {
  const [folderId, setFolderId] = useState("");
  const [fileName, setFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [error, setError] = useState("");
  const [needsScopeUpgrade, setNeedsScopeUpgrade] = useState(false);
  const [driveUrl, setDriveUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const { addNotification } = useNotification();
  const classes = useGlobalStyles();

  // Check if video already has a Google Drive URL and set initial state
  useEffect(() => {
    if (video) {
      const videoName =
        video.name ||
        ("video_title" in video
          ? video.video_title
          : `video-${video.heygenId}.mp4`);
      setFileName(videoName);

      if (video.googleDriveUrl) {
        setDriveUrl(video.googleDriveUrl);
      }
    }
  }, [video]);

  useEffect(() => {
    const checkScope = async () => {
      if (open) {
        const hasScope = hasGoogleDriveScope();
        setNeedsScopeUpgrade(!hasScope);
      }
    };
    checkScope();
  }, [open]);

  const getVideoUrl = (): string => {
    if (overrideVideoUrl) return overrideVideoUrl;
    if (!video) return "";

    // Check if it's a regular GeneratedVideo
    if ("withCaptions" in video) {
      return video.withCaptions
        ? video.video_url_caption || ""
        : video.video_url || "";
    }
    // It's an IVAvatarVideo
    else {
      return video.video_url || "";
    }
  };

  const getVideoName = (): string => {
    if (!video) return "";
    return (
      video.name ||
      ("video_title" in video
        ? video.video_title
        : `video-${video.heygenId}.mp4`)
    );
  };

  const handleUpload = useCallback(
    async (isRetry = false) => {
      if (!video) return;

      try {
        setIsUploading(true);
        setUploadMessage("");
        setError("");

        const videoUrl = getVideoUrl();
        if (!videoUrl) {
          throw new Error("Video URL not found");
        }

        // Get Google access token
        const accessToken = localStorage.getItem("google_access_token");
        if (!accessToken) {
          throw new Error(
            "Google access token not found. Please sign in with Google again."
          );
        }

        // Check if we have the drive scope
        if (!hasGoogleDriveScope()) {
          setNeedsScopeUpgrade(true);
          throw new Error(
            "Google Drive access requires additional permissions."
          );
        }

        const result = await uploadVideoToGoogleDrive(
          accessToken,
          videoUrl,
          fileName,
          folderId || null
        );

        if (result.data && result.data.viewLink) {
          setDriveUrl(result.data.viewLink);

          // Update the appropriate video type in the database
          if ("withCaptions" in video) {
            // It's a regular GeneratedVideo
            await updateGeneratedVideoWithDriveUrl(
              video._id,
              result.data.viewLink
            );
          } else {
            // It's an IVAvatarVideo
            await updateIVAvatarVideoWithDriveUrl(
              video._id,
              result.data.viewLink
            );
          }

          if (onDriveUploadSuccess) {
            onDriveUploadSuccess(video._id, result.data.viewLink);
          }
        }

        setUploadMessage("Video uploaded to Google Drive successfully!");
        setRetryCount(0);
      } catch (error: any) {
        console.error("Upload error:", error);

        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to upload video. Please try again.";
        setError(errorMessage);

        if (
          errorMessage.toLowerCase().includes("expired") &&
          retryCount < 1 &&
          !isRetry
        ) {
          addNotification("Token expired. Refreshing and retrying...", "info");
          setRetryCount((prev) => prev + 1);
          setTimeout(() => {
            handleRequestScopeUpgradeForRetry();
          }, 1000);
        } else {
          addNotification(errorMessage, "error");
        }
      } finally {
        setIsUploading(false);
      }
    },
    [
      video,
      fileName,
      folderId,
      retryCount,
      onDriveUploadSuccess,
      addNotification,
    ]
  );

  const handleRequestScopeUpgradeForRetry = () => {
    if (!video) return;

    const currentPath = window.location.pathname;
    localStorage.setItem("pending_drive_retry", "true");
    localStorage.setItem(
      "retry_upload_data",
      JSON.stringify({
        fileName,
        folderId,
        videoId: video._id,
        videoType: "withCaptions" in video ? "regular" : "avatarLab",
      })
    );
    initiateGoogleDriveSignIn(currentPath);
  };

  useEffect(() => {
    const pendingRetry = localStorage.getItem("pending_drive_retry");
    const retryDataStr = localStorage.getItem("retry_upload_data");

    if (pendingRetry === "true" && retryDataStr) {
      try {
        const retryData = JSON.parse(retryDataStr);
        localStorage.removeItem("pending_drive_retry");
        localStorage.removeItem("retry_upload_data");

        if (video && video._id === retryData.videoId) {
          setTimeout(() => {
            handleUpload(true);
          }, 500);
        }
      } catch (e) {
        console.error("Error parsing retry data:", e);
      }
    }
  }, [open, video, fileName, folderId, handleUpload]);

  const handleRequestScopeUpgrade = () => {
    const currentPath = window.location.pathname;
    localStorage.setItem("pending_drive_scope", "true");
    initiateGoogleDriveSignIn(currentPath);
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(driveUrl);
      setCopied(true);
      addNotification("Google Drive URL copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy URL: ", err);
      addNotification("Failed to copy URL", "error");
    }
  };

  const resetForm = () => {
    setFolderId("");
    setUploadMessage("");
    setError("");
    setCopied(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Check for pending scope upgrade on component mount
  useEffect(() => {
    const pendingScope = localStorage.getItem("pending_drive_scope");
    if (pendingScope === "true") {
      localStorage.removeItem("pending_drive_scope");
      setGoogleDriveScopeGranted();
      setNeedsScopeUpgrade(false);
      addNotification("Google Drive access granted!", "success");
    }
  }, []);

  // Check if video already has a Google Drive URL when modal opens
  const hasExistingDriveUrl = video?.googleDriveUrl || driveUrl;

  if (needsScopeUpgrade) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Google Drive Permissions Required</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              To upload videos to Google Drive, we need additional permissions.
            </Alert>
            <Typography variant="body2">
              Click "Grant Access" to sign in and grant Google Drive
              permissions.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleRequestScopeUpgrade}
            variant="contained"
            color="primary"
          >
            Grant Access
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!video) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <AddToDriveIcon />
          {hasExistingDriveUrl ? "Google Drive Link" : "Upload to Google Drive"}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {hasExistingDriveUrl ? "Video already uploaded: " : "Uploading: "}
            <strong>{getVideoName()}</strong>
            {"withCaptions" in video && (
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (Regular Video)
              </Typography>
            )}
            {!("withCaptions" in video) && (
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (Avatar Lab Video)
              </Typography>
            )}
          </Typography>

          {!hasExistingDriveUrl && (
            <>
              <TextField
                label="File Name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                fullWidth
                margin="normal"
                required
                disabled={!!driveUrl}
                className={classes.textFieldLabel}
              />

              <Tooltip
                title="Copy the Folder ID from Google Drive URL: https://drive.google.com/drive/folders/FOLDER_ID_HERE"
                placement="top"
                arrow
              >
                <TextField
                  label="Folder ID (optional)"
                  value={folderId}
                  onChange={(e) => setFolderId(e.target.value)}
                  placeholder="Enter Google Drive folder ID"
                  fullWidth
                  margin="normal"
                  className={classes.textFieldLabel}
                  helperText={
                    <Typography variant="caption">
                      Leave empty to upload to Google Drive root. To get folder
                      ID: open folder in Google Drive → copy ID from URL
                    </Typography>
                  }
                  disabled={!!driveUrl}
                />
              </Tooltip>
            </>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {uploadMessage && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadMessage}
            </Alert>
          )}

          {hasExistingDriveUrl && (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Google Drive URL:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    wordBreak: "break-all",
                    fontSize: "0.8rem",
                  }}
                >
                  {video.googleDriveUrl || driveUrl}
                </Typography>
                <IconButton
                  onClick={handleCopyUrl}
                  size="small"
                  color={copied ? "success" : "primary"}
                >
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Box>
            </Box>
          )}

          {!hasExistingDriveUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary">
                <strong>Note:</strong> You must be signed in with Google to
                upload to Drive. The video will be uploaded to your connected
                Google account.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          Cancel
        </Button>
        {hasExistingDriveUrl ? (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        ) : !driveUrl ? (
          <Button
            onClick={() => handleUpload()}
            variant="contained"
            disabled={isUploading || !fileName}
            startIcon={isUploading ? <CircularProgress size={16} /> : null}
          >
            {isUploading ? "Uploading..." : "Upload to Google Drive"}
          </Button>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoogleDriveUploadModal;
